import type { DatasheetBlockedPoint, DatasheetBlockedReasonCode } from '../types';

/** Label shown on any point running on a manually-entered tolerance with no catalog backing. */
export const NO_CATALOG_BACKING_LABEL = 'Manual tolerance — no catalog backing';

/** Longer explanation for the no-catalog-backing flag (builder tooltip / run page). */
export const noCatalogBackingHint = (): string =>
  'This point runs on a manually-entered tolerance. No catalog range backs it, so it is not verified against equipment specs.';

/** Whether a catalog-gap reason can be rescued by a manual tolerance override (STUB cannot). */
export const isOverrideRescuable = (
  reasonCode: DatasheetBlockedReasonCode
): boolean => reasonCode !== 'STUB_ITEM';

/** Actionable fix instruction for a blocked datasheet point (spec: "how to fix", not just "broken"). */
export const blockedPointHint = (
  point: DatasheetBlockedPoint,
  catalogName: string | null
): string => {
  const catalog = catalogName ?? 'the equipment catalog';
  switch (point.reasonCode) {
    case 'STUB_ITEM':
      return 'Unresolved stub from procedure ingestion — replace the STUB step in the Builder, or remove the item.';
    case 'FUNCTION_MISSING':
      return `Catalog '${catalog}' has no '${point.detail.functionId}' specs — add the function with its accuracy data.`;
    case 'RANGE_UNRESOLVED':
      return `No '${point.detail.functionId}' range covers ${point.detail.nominal} ${point.detail.unit} — extend the range table.`;
    case 'ACCURACY_MISSING':
      return `The matching '${point.detail.functionId}' range is missing accuracyPct / accuracyCounts / resolution in catalog '${catalog}'.`;
    case 'NO_CATALOG':
      return 'No equipment catalog exists to resolve tolerances from — create one (e.g., via procedure ingestion).';
    case 'NO_COVERING_CATALOG':
      return 'No single equipment catalog covers this group — extend a catalog or fix the group ids.';
    case 'AMBIGUOUS_CATALOG':
      return 'Multiple equipment catalogs cover this group — the tolerance source is ambiguous; consolidate the catalogs.';
  }
};

export const catalogIssueBanner = (
  reasonCode: 'NO_CATALOG' | 'NO_COVERING_CATALOG' | 'AMBIGUOUS_CATALOG',
  detail: string[]
): string => {
  switch (reasonCode) {
    case 'NO_CATALOG':
      return 'No equipment catalog exists to resolve tolerances from — no group can run.';
    case 'NO_COVERING_CATALOG':
      return `No single equipment catalog covers all of the script's function groups (${detail.join(', ')}).`;
    case 'AMBIGUOUS_CATALOG':
      return `Multiple equipment catalogs cover the script's function groups — the tolerance source is ambiguous (${detail.join(', ')}).`;
  }
};
